import { Calendar } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

import { ButtonNext } from "../../ecommerce/components/components/button-next";
import { ChatOptionComponentProps } from "../lookup";
import { BottomDrawerResponsive } from "~/components/bottom-drawer/responsive";
import {
  DatePickerMonthYear,
  DatePickerMonthYearRef,
} from "~/components/input/date-picker/date-picker-month-year";
import { Text } from "~/components/text";
import { useT } from "~/i18n/use-t";
import { cn } from "~/util/cn";

export const OptionalDatePickerMonthYear: React.FC<
  ChatOptionComponentProps
> = ({ choices, sendResponse }) => {
  const t = useT();
  const [selection, setSelection] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const datePickerMonthYearRef = useRef<DatePickerMonthYearRef>(null);

  useEffect(() => {
    if (choices) {
      setIsSubmitted(false);
      setSelection(null);
      datePickerMonthYearRef.current?.reset();
    }
  }, [choices]);

  const no = t({ tx: "graph.text.no" });
  const suspect = t({ tx: "graph.text.suspect" });
  const yes = t({ tx: "graph.text.yes" });

  const handleSubmit = (choice: string) => {
    sendResponse({
      message: choice,
      selection: [choice],
    });
    setIsSubmitted(true);
  };

  return (
    <div className="flex w-full max-w-screen-sm flex-wrap items-center justify-center gap-2">
      {!isSubmitted ? (
        <>
          <button
            title={no}
            className={cn(
              "btn-outline btn flex-1 border-neutral/40 shadow-sm",
              selection === no && "btn-primary text-white",
            )}
            onClick={() => handleSubmit(no)}
          >
            <Text text={no} />
          </button>
          <button
            title={suspect}
            className={cn(
              "btn-outline btn flex-1 border-neutral/40 shadow-sm",
              selection === suspect && "btn-primary text-white",
            )}
            onClick={() => handleSubmit(suspect)}
          >
            <Text text={suspect} />
          </button>
        </>
      ) : null}

      <BottomDrawerResponsive
        button={({ toggle }) =>
          isSubmitted ? (
            <ButtonNext loading className="btn-disabled flex-1" />
          ) : (
            <button
              title={yes}
              className="btn-primary btn flex-grow shadow-sm"
              onClick={toggle}
            >
              <span>
                <Text text={yes} />
                {selection && ![no, suspect].includes(selection) ? ", " : null}
              </span>
              {selection && ![no, suspect].includes(selection) && (
                <span className="flex items-center">{selection}</span>
              )}
              <Calendar width={16} className="ml-1.5" />
            </button>
          )
        }
      >
        {({ close }) => (
          <div className="p-4">
            <DatePickerMonthYear
              ref={datePickerMonthYearRef}
              onSelect={({ year, monthLong }) =>
                setSelection(`${monthLong} ${year}`)
              }
              onUnselect={() => setSelection(null)}
            />
            <div className="mt-4 flex items-center justify-end">
              {selection ? (
                <button
                  title={t({ tx: "graph.text.next" })}
                  onClick={() => {
                    handleSubmit(selection);
                    close();
                  }}
                  className="btn-primary btn gap-x-0"
                >
                  <Text tx="graph.text.yes" />
                  {", "}
                  {selection}
                </button>
              ) : (
                <button
                  title={t({ tx: "graph.text.next" })}
                  disabled
                  className="btn-primary btn"
                >
                  <Text tx="graph.text.next" />
                </button>
              )}
            </div>
          </div>
        )}
      </BottomDrawerResponsive>
    </div>
  );
};
